import { useState, useMemo } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { Search, UserPlus, X, Shield, ShieldOff, Loader2 } from 'lucide-react'
import { useMiembrosGrupo, useAgregarMiembro, useQuitarMiembro, useCambiarRol } from '@/core/hooks/useChat'
import { useUsuariosOrg } from '@/hooks/useUsuarios'
import { useUserNameResolver } from '@/core/hooks/useUserNameResolver'
import { useChatContext } from '@/core/providers/ChatProvider'
import { cn } from '@/lib/utils'

interface GrupoMiembrosDialogProps {
  conversacionId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  isAdmin: boolean
}

export function GrupoMiembrosDialog({ conversacionId, open, onOpenChange, isAdmin }: GrupoMiembrosDialogProps) {
  const { data: miembros = [], isLoading } = useMiembrosGrupo(conversacionId)
  const { data: usuarios = [] } = useUsuariosOrg({ activo: true })
  const agregarMiembro = useAgregarMiembro()
  const quitarMiembro = useQuitarMiembro()
  const cambiarRol = useCambiarRol()
  const resolveNombre = useUserNameResolver()
  const { sendMemberChange } = useChatContext()

  const [showAdd, setShowAdd] = useState(false)
  const [busqueda, setBusqueda] = useState('')

  const adminCount = miembros.filter((m) => m.role === 'admin').length

  const availableUsers = useMemo(() => {
    const memberEmails = new Set(miembros.map((m) => m.user_email))
    return usuarios
      .filter((u) => !memberEmails.has(u.email))
      .filter((u) => !busqueda || u.email.toLowerCase().includes(busqueda.toLowerCase()) || (u.nombre ?? '').toLowerCase().includes(busqueda.toLowerCase()))
  }, [usuarios, miembros, busqueda])

  const handleAdd = (email: string) => {
    agregarMiembro.mutate({ conversacionId, email }, {
      onSuccess: () => sendMemberChange(conversacionId),
    })
    setShowAdd(false)
    setBusqueda('')
  }

  const handleRemove = (email: string) => {
    quitarMiembro.mutate({ conversacionId, email }, {
      onSuccess: () => sendMemberChange(conversacionId),
    })
  }

  const handleToggleRole = (email: string, currentRole: 'admin' | 'member') => {
    cambiarRol.mutate({
      conversacionId,
      email,
      role: currentRole === 'admin' ? 'member' : 'admin',
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-base">Miembros ({miembros.length})</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Add member button */}
            {isAdmin && !showAdd && (
              <Button variant="outline" size="sm" className="w-full" onClick={() => setShowAdd(true)}>
                <UserPlus className="h-4 w-4 mr-2" />
                Agregar miembro
              </Button>
            )}

            {/* Add member form */}
            {showAdd && (
              <div className="space-y-2 border rounded-lg p-2 bg-muted/30">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      value={busqueda}
                      onChange={(e) => setBusqueda(e.target.value)}
                      placeholder="Buscar usuario..."
                      className="pl-7 h-8 text-sm"
                      autoFocus
                    />
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => { setShowAdd(false); setBusqueda('') }}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <ScrollArea className="max-h-32">
                  <div className="space-y-0.5">
                    {availableUsers.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-2">Sin usuarios disponibles</p>
                    ) : (
                      availableUsers.map((u) => (
                        <button
                          key={u.id}
                          onClick={() => handleAdd(u.email)}
                          disabled={agregarMiembro.isPending}
                          className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm hover:bg-accent text-left"
                        >
                          <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
                            {(u.nombre || u.email)[0]?.toUpperCase()}
                          </div>
                          <span className="truncate">{u.nombre || u.email.split('@')[0]}</span>
                        </button>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* Members list */}
            <ScrollArea className="max-h-64">
              <div className="space-y-1">
                {miembros.map((m) => {
                  const nombre = resolveNombre(m.user_email)
                  const isOnlyAdmin = m.role === 'admin' && adminCount <= 1

                  return (
                    <div
                      key={m.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent/50"
                    >
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">
                        {nombre[0]?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{nombre}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{m.user_email}</p>
                      </div>

                      {m.role === 'admin' && (
                        <Badge variant="secondary" className="text-[10px] shrink-0">Admin</Badge>
                      )}

                      {/* Actions (only for admins) */}
                      {isAdmin && (
                        <div className="flex gap-0.5 shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn('h-7 w-7', isOnlyAdmin && 'opacity-30')}
                            onClick={() => handleToggleRole(m.user_email, m.role)}
                            disabled={isOnlyAdmin || cambiarRol.isPending}
                            title={m.role === 'admin' ? 'Quitar admin' : 'Hacer admin'}
                          >
                            {m.role === 'admin' ? <ShieldOff className="h-3.5 w-3.5" /> : <Shield className="h-3.5 w-3.5" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn('h-7 w-7 text-destructive hover:text-destructive', isOnlyAdmin && 'opacity-30')}
                            onClick={() => handleRemove(m.user_email)}
                            disabled={isOnlyAdmin || quitarMiembro.isPending}
                            title="Quitar del grupo"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </ScrollArea>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
